import { useMemo, useEffect } from 'react';
import { useReglasComision } from '../../hooks/useReglasComision';

// Importaciones del ecosistema centralizado de Seguros (Canvas)
import { parseCurrency, formatCurrencyForInput, normalizeString } from '../../../utils/segurosUtils';
import { checkEsCaucionEspecial } from './policyUtils';

// Importación del formateador de medios de pago unificado (Reutilización Senior)
import { formatMedioPagoAbreviado } from '../../../utils/paymentService';

/**
 * Hook especializado en la lógica de negocio del formulario de pólizas.
 * Protegido contra valores nulos o indefinidos de los datos maestros globales para garantizar cero crashes.
 */
export const usePolicyLogic = ({
    formData,
    setFormData,
    dbMasters,
    dbSubcanales = [],
    activeClient,
    riesgoObj,
    canalObj,
    subcanalObj,
    mode,
    initialData,
    isHydrated
}) => {

    // Extraemos de forma súper segura las listas maestras con fallbacks de arreglos vacíos
    const listCanales = dbMasters?.canales || [];
    const listCompanias = dbMasters?.companias || [];
    const listRiesgos = dbMasters?.riesgos || [];
    const listAcuerdos = dbMasters?.acuerdos || [];
    const listReglasRenovacion = dbMasters?.reglasRenovacion || [];

    // --- 1. FLAGS DE RIESGO (ESTADO DERIVADO) ---
    const riskFlags = useMemo(() => {
        const r = normalizeString(formData.riesgo);
        const esCaucionEspecial = checkEsCaucionEspecial(formData.riesgo, formData.subriesgo);
        
        return {
            esVehiculo: ['automotores', 'motovehiculos', 'camion'].includes(r),
            esAP: r === 'accidentes personales',
            esMalaPraxis: r === 'mala praxis medica',
            requiereUbicacion: ['incendio', 'integral de comercio', 'combinado familiar', 'responsabilidad civil'].includes(r),
            esCaucionEspecial,
            showEndoso0: esCaucionEspecial
        };
    }, [formData.riesgo, formData.subriesgo]);

    // --- 2. LÓGICA DE FACTURACIÓN (REGLAS A, B, C y D) ---
    const billingLogic = useMemo(() => {
        const forma = normalizeString(formData.formaPago);
        const factu = normalizeString(formData.tipoFacturacion);
        
        const cuotasList = ['bimestral', 'trimestral', 'cuatrimestral', 'semestral', 'anual', 'bianual', 'trianual'];
        const esMultiCuota = cuotasList.includes(factu);
        const esRefacturacion = riskFlags.showEndoso0;

        const permiteDetalle = forma.includes('tarjeta') || 
                               forma.includes('cbu') || 
                               forma.includes('adelanto') || 
                               forma.includes('credito');

        const showPrimeraCuota = esMultiCuota && !esRefacturacion && 
            (forma.includes('tarjeta') || forma.includes('cbu') || forma.includes('transferencia') || forma.includes('cupones')) && 
            !forma.includes('adelanto');

        const showAdelanto = esMultiCuota && !esRefacturacion && forma.includes('adelanto');

        const requiereRegistroCobro = !esRefacturacion && ['transferencia', 'cupones', 'adelanto'].some(f => forma.includes(f));

        return {
            esAfip: forma === 'debito afip',
            showMainPremiumFields: !esRefacturacion,
            showTipoRenovacion: !esRefacturacion,
            showPrimeraCuota,
            showAdelanto,
            mostrarCuotas: esMultiCuota,
            permiteDetalle,
            requiereRegistroCobro
        };
    }, [formData.formaPago, formData.tipoFacturacion, riskFlags.showEndoso0]);

    // --- 3. LISTAS FILTRADAS PARA LA UI (Súper seguras) ---
    const subriesgosDisponibles = useMemo(() => {
        if (!formData.riesgo || listRiesgos.length === 0) return [];
        const r = listRiesgos.find(x => normalizeString(x.nombre) === normalizeString(formData.riesgo));
        return r?.subriesgos || [];
    }, [formData.riesgo, listRiesgos]);

    const companiaLists = useMemo(() => {
        if (!formData.compania || listCompanias.length === 0) return { organizadores: [], productores: [] };
        const cia = listCompanias.find(c => normalizeString(c.nombre) === normalizeString(formData.compania));
        if (!cia?.codigos) return { organizadores: [], productores: [] };
        return {
            organizadores: [...new Set(cia.codigos.map(x => x.organizador).filter(Boolean))],
            productores: [...new Set(cia.codigos.map(x => x.pas).filter(Boolean))]
        };
    }, [formData.compania, listCompanias]);

    // Generamos las opciones del dropdown de tarjetas/CBUs delegando en paymentService
    const listaMedios = useMemo(() => {
        if (!billingLogic.permiteDetalle) return [];
        const forma = normalizeString(formData.formaPago);
        let options = [];
        
        if (activeClient) {
            if (forma.includes('cbu')) {
                options = (activeClient.cbus || []).map(c => {
                    const formatted = formatMedioPagoAbreviado(c, 'CBU');
                    return { id: formatted, label: formatted };
                });
            } else if (forma.includes('tarjeta') || forma.includes('credito')) {
                options = (activeClient.tarjetas || []).map(t => {
                    const formatted = formatMedioPagoAbreviado(t, 'Tarjeta de Crédito');
                    return { id: formatted, label: formatted };
                });
            }
        }

        // Si la póliza ya tenía un medio de pago asignado que no figura en la lista actual
        if (formData.medioPago && !options.find(opt => opt.id === formData.medioPago)) {
            const legacyFormatted = formatMedioPagoAbreviado(formData.medioPago, formData.formaPago);
            options.unshift({ id: legacyFormatted, label: legacyFormatted });
        }

        return options;
    }, [activeClient, billingLogic.permiteDetalle, formData.formaPago, formData.medioPago]);

    // --- 4. REGLAS DE COMISIÓN EXTERNAS ---
    const { 
        comisionCanal, comisionSubcanal, 
        descuentoCanal, descuentoSubcanal, 
        habilitarEdicion 
    } = useReglasComision(
        canalObj, 
        subcanalObj, 
        riesgoObj?.group || 'PATRIMONIALES', 
        formData.compania
    );

    // ◄ SINCRO RESET DE ANCLAJE MANUAL (Senior Safeguard):
    // Si el usuario cambia activamente el Canal, Subcanal o Compañía en la UI,
    // desactivamos el override manual para que vuelvan a cargarse los valores por defecto del nuevo acuerdo.
    useEffect(() => {
        if (!isHydrated) return;
        setFormData(prev => {
            if (prev.manualCanalComisionOverride) {
                return { ...prev, manualCanalComisionOverride: false };
            }
            return prev;
        });
    }, [formData.canal, formData.subcanal, formData.compania, isHydrated, setFormData]);

    useEffect(() => {
        if (!isHydrated) return;
        
        // ◄ SALVAGUARDA DE VALOR MANUAL (Senior Protection):
        // Si el usuario modificó manualmente las comisiones/descuentos del canal o subcanal,
        // evitamos que este efecto automático pise sus cambios con las reglas por defecto de la base de datos.
        if (formData.manualCanalComisionOverride) return;

        const cambios = {};
        if (comisionCanal !== '' && parseFloat(formData.comisionOrganizador) !== parseFloat(comisionCanal)) cambios.comisionOrganizador = comisionCanal;
        if (comisionSubcanal !== '' && parseFloat(formData.comisionAsociado) !== parseFloat(comisionSubcanal)) cambios.comisionAsociado = comisionSubcanal;
        if (descuentoCanal !== '' && parseFloat(formData.descuentoOrganizador) !== parseFloat(descuentoCanal)) cambios.descuentoOrganizador = descuentoCanal;
        if (descuentoSubcanal !== '' && parseFloat(formData.descuentoAsociado) !== parseFloat(descuentoSubcanal)) cambios.descuentoAsociado = descuentoSubcanal;
        
        if (Object.keys(cambios).length > 0) {
            setFormData(prev => ({ ...prev, ...cambios }));
        }
    }, [
        comisionCanal, comisionSubcanal, descuentoCanal, descuentoSubcanal, 
        isHydrated, setFormData, formData.comisionOrganizador, formData.comisionAsociado, 
        formData.descuentoOrganizador, formData.descuentoAsociado,
        formData.manualCanalComisionOverride // Agregado como dependencia para consistencia reactiva
    ]);

    // --- 5. AUTOMATIZACIÓN DE PREFIJOS ---
    useEffect(() => {
        if (!isHydrated) return;
        
        const mapping = { 
            'PENDIENTE DE EMISIÓN': 'PE-', 
            'PENDIENTE DE INSPECCIÓN': 'PI-', 
            'PROPUESTA INGRESADA': 'PIN-' 
        };
        
        const prefix = mapping[formData.estadoEmision];
        
        if (prefix) {
            if (!formData.nPoliza.startsWith(prefix)) {
                const randomNum = Math.floor(10000 + Math.random() * 90000);
                setFormData(p => ({ ...p, nPoliza: `${prefix}${randomNum}` }));
            }
        } else {
            if (formData.nPoliza.includes('-') && !initialData?.id) {
                setFormData(p => ({ ...p, nPoliza: '' }));
            }
        }
    }, [formData.estadoEmision, isHydrated, initialData?.id, setFormData, formData.nPoliza]);

    // --- 6. ACUERDOS DE COMISIÓN (Súper Seguros) ---
useEffect(() => {
    // 1. AGREGA ESTA LÍNEA: Si el usuario modificó a mano, frenamos el recálculo automático
    if (formData.manualComisionOverride) return;

    if (!isHydrated || listAcuerdos.length === 0) return;
    const cia = normalizeString(formData.compania);
    const ries = normalizeString(formData.riesgo);
    const subr = normalizeString(formData.subriesgo);
    const pas = normalizeString(formData.pas);

    if (!cia || !ries) {
        if (parseFloat(formData.comision) !== 0) setFormData(p => ({ ...p, comision: 0 }));
        return;
    }

    // Busqueda excepcional: Compañía + Riesgo + Subriesgo + PAS
    let acuerdo = listAcuerdos.find(a => 
        ((a.companiaId && a.companiaId === formData.companiaId) || normalizeString(a.compania) === cia) && 
        ((a.riesgoId && a.riesgoId === formData.riesgoId) || normalizeString(a.riesgo) === ries) && 
        normalizeString(a.subriesgo) === subr &&
        (!a.pas || normalizeString(a.pas) === pas)
    );

    // Herencia/Fallback: Regla general del ramo (Sin subriesgo)
    if (!acuerdo) {
        acuerdo = listAcuerdos.find(a => 
            ((a.companiaId && a.companiaId === formData.companiaId) || normalizeString(a.compania) === cia) && 
            ((a.riesgoId && a.riesgoId === formData.riesgoId) || normalizeString(a.riesgo) === ries) && 
            (!a.subriesgo || a.subriesgo === '') &&
            (!a.pas || normalizeString(a.pas) === pas)
        );
    }

    const nuevaCom = acuerdo ? (acuerdo.comision || acuerdo.porcentaje || 0) : 0;
    if (parseFloat(formData.comision) !== parseFloat(nuevaCom)) {
        setFormData(p => ({ ...p, comision: parseFloat(nuevaCom) }));
    }
}, [
    formData.compania, 
    formData.companiaId, 
    formData.riesgo, 
    formData.riesgoId, 
    formData.subriesgo, 
    formData.pas, 
    listAcuerdos, 
    isHydrated,
    setFormData,
    formData.comision,
    formData.manualComisionOverride // 2. AGREGAR A LAS DEPENDENCIAS
]);

    // --- 7. REGLAS DE RENOVACIÓN (Súper Seguros) ---
    useEffect(() => {
        if (!isHydrated || listReglasRenovacion.length === 0) return;

        // Override manual activo: respetamos la decisión del usuario en la UI
        if (formData.manualRenovacionOverride) return;

        const comp = normalizeString(formData.compania);
        const ries = normalizeString(formData.riesgo);
        const subr = normalizeString(formData.subriesgo);

        if (!comp || !ries) return;

        const regla = listReglasRenovacion.find(rg => {
            const matchCia = 
                (rg.companiaId && rg.companiaId === formData.companiaId) || 
                normalizeString(rg.compania) === comp || 
                normalizeString(rg.compania) === 'todas';
            const matchRies = 
                (rg.riesgoId && rg.riesgoId === formData.riesgoId) || 
                normalizeString(rg.riesgo) === ries;
            const matchSubr = !rg.subriesgo || normalizeString(rg.subriesgo) === '' || normalizeString(rg.subriesgo) === subr;
            return matchCia && matchRies && matchSubr;
        });

        const nuevaForma = regla ? regla.formaRenovacion : 'MANUAL';
        if (formData.formaRenovacion !== nuevaForma) {
            setFormData(prev => ({ ...prev, formaRenovacion: nuevaForma }));
        }
    }, [
        formData.compania, 
        formData.companiaId, 
        formData.riesgo, 
        formData.riesgoId, 
        formData.subriesgo, 
        formData.formaRenovacion, 
        formData.manualRenovacionOverride, 
        listReglasRenovacion, 
        isHydrated,
        setFormData
    ]);

    // --- 8. SINCRONIZACIÓN DE VIGENCIA CAUCIÓN ESPECIAL ---
    useEffect(() => {
        if (isHydrated && riskFlags.showEndoso0 && formData.inicioVigencia) {
            const yearPart = formData.inicioVigencia.split('-')[0];
            
            if (formData.inicioVigencia.length === 10 && parseInt(yearPart) > 1900) {
                setFormData(prev => {
                    if (prev.ref_inicioVigencia !== formData.inicioVigencia) {
                        return { ...prev, ref_inicioVigencia: formData.inicioVigencia };
                    }
                    return prev;
                });
            }
        }
    }, [formData.inicioVigencia, riskFlags.showEndoso0, isHydrated, setFormData]);

    // --- 9. CÁLCULO DE IMPORTES DE CUOTAS ---
    useEffect(() => {
        if (!isHydrated) return;

        const { showAdelanto, showPrimeraCuota } = billingLogic;
        if ((showAdelanto || showPrimeraCuota) && formData.primaEmitida && formData.cuotas > 0) {
            const valNum = parseCurrency(formData.primaEmitida) / (parseInt(formData.cuotas) || 1);
            const fmt = formatCurrencyForInput(valNum);
            
            if (formData.primaAdelanto !== (showAdelanto ? fmt : '') || 
                formData.primaPrimeraCuota !== (showPrimeraCuota ? fmt : '')) {
                setFormData(prev => ({
                    ...prev,
                    primaAdelanto: showAdelanto ? fmt : '',
                    primaPrimeraCuota: showPrimeraCuota ? fmt : ''
                }));
            }
        } else if (formData.primaAdelanto !== '' || formData.primaPrimeraCuota !== '') {
            setFormData(prev => ({ ...prev, primaAdelanto: '', primaPrimeraCuota: '' }));
        }
    }, [formData.primaEmitida, formData.cuotas, billingLogic.showAdelanto, billingLogic.showPrimeraCuota, isHydrated, setFormData, formData.primaAdelanto, formData.primaPrimeraCuota]);

    return {
        riskFlags,
        billingLogic,
        subriesgosDisponibles,
        companiaLists,
        listaMedios,
        habilitarEdicion
    };
};